<div class="container intro header">
  <div>
    <h1 class="toolbar-text header-text">
      O que entendemos por educação de qualidade
    </h1>
  </div>
</div>

<div class="principios-qualidade">
  <div>
    <img
      mat-card-image
      src="../../../assets/imagens/qualidade/curve_01_quality.svg"
      alt="Curva decorativa 01"
    />
  </div>
  <div>
    <h1>Princípios e pressupostos de uma educação de qualidade</h1>
  </div>
  <div class="main">
    <img
      mat-card-image
      src="../../../assets/imagens/qualidade/principles_of_quality_education.svg"
    />
  </div>
</div>

<div class="porque-educacao-qualidade">
  <img
    src="../../../assets/imagens/qualidade/curve_02_quality.svg"
    alt="Curva decorativa 02"
  />
  <div class="content">
    <h1>Por que educação de qualidade?</h1>
    <p>
      Somente a educação de qualidade favorece o alcance dos objetivos
      educacionais, expressos, em síntese, no Art. 205 da Constituição Federal,
      quais sejam:
      <b>
        “[...] pleno desenvolvimento da pessoa, seu preparo para o exercício da
        cidadania e sua qualificação para o trabalho”
      </b>
      (BRASIL, 1988).
    </p>
  </div>
  <img
    src="../../../assets/imagens/qualidade/curve_03_quality.svg"
    alt="Curva decorativa 03"
  />
</div>

<div class="elementos-educacao-qualidade container">
  <div class="primeira-explicacao">
    <img src="../../../assets/imagens/qualidade/quality_education.svg" />
    <div class="spacer"></div>
    <div>
      <h1>O que é educação de qualidade?</h1>
      <p>
        Educação de qualidade é um conceito que dever ser construído
        socialmente, levando em consideração o contexto histórico e cultural
        (OLIVEIRA; ARAÚJO, 2005; CURY, 2014); e que se altera de acordo com o
        tempo e o espaço (DOURADO; OLIVEIRA; 2009).
      </p>
      <p>
        Em uma perspectiva histórica e social, no Brasil, conforme Oliveira e
        Araujo (2005, p.8), três significados distintos de qualidade foram
        “construídos e circularam simbólica e concretamente na sociedade: um
        primeiro,
        <b
          >condicionado pela oferta limitada de oportunidades de
          escolarização;</b
        >
        um segundo, relacionado à ideia de fluxo, definido como número de alunos
        que progridem ou não dentro de determinado sistema de ensino; e,
        finalmente, a ideia de qualidade associada à
        <b>aferição de desempenho</b> mediante testes em larga escala” (grifos
        nossos).
      </p>
    </div>
  </div>
  <p>
    Nesse sentido, ainda em consonância com Oliveira e Araujo (2005), defendemos
    que uma educação de qualidade, no momento histórico atual, em face dos
    direitos e princípios constitucionais estabelecidos, deve ter um significado
    que englobe:
  </p>
  <div class="steps">
    <div class="step-1">
      <img src="../../../assets/imagens/qualidade/number_01_quality.svg" />
      <p>A garantia de Acesso</p>
    </div>
    <div class="step-2">
      <img src="../../../assets/imagens/qualidade/number_02_quality.svg" />
      <p>Condições adequadas de permanência e aprendizagem</p>
    </div>
    <div class="step-3">
      <img src="../../../assets/imagens/qualidade/number_03_quality.svg" />
      <p>
        Promoção do desenvolvimento do ponto de vista humano, político e social
      </p>
    </div>
    <div class="step-4">
      <img src="../../../assets/imagens/qualidade/number_04_quality.svg" />
      <p>Emancipação e inserção social</p>
    </div>
    <div class="step-5">
      <img src="../../../assets/imagens/qualidade/number_05_quality.svg" />
      <p>
        Não (re)produção de mecanismos de diferenciação e de exclusão social
      </p>
    </div>
  </div>
  <div class="ultima-explicacao">
    <p>
      Em outra perspectiva, mais próxima da gestão e do financiamento das
      escolas, Rubens Camargo et al. (INEP, 2006) destacam que educação de
      qualidade é um conceito complexo, uma vez que engloba o (1)
      <b>custeio adequado</b>
      das atividades escolares como aspecto fundamental e meio de garantir a
      aquisição de insumos em quantidades e variedades apropriadas para
      assegurar condições objetivas para a oferta de uma educação efetiva;
      articulado à (2)
      <b>valorização dos sujeitos</b>
      envolvidos no processo de ensino e aprendizagem nas atividades de
      organização e gestão da escola; (3) atenção aos
      <b>aspectos pedagógicos</b>; e à (4) consideração das expectativas da
      comunidade escolar quanto à <b>aquisição dos saberes</b> ministrados pela
      escola (grifos nossos).
    </p>
    <img src="../../../assets/imagens/qualidade/educated_brazil.svg" />
  </div>
</div>

<div class="aspectos-dimensao-qualidade">
  <img src="../../../assets/imagens/qualidade/curve_04_quality.svg" />
  <div class="content">
    <div class="header">
      <h1>Aspectos e dimensões da qualidade</h1>
      <p>
        Em se tratando de educação, qualidade tem diferentes aspectos ou
        dimensões (ADAMS, 1993):
      </p>
    </div>
    <div class="body">
      <div>
        <img src="../../../assets/imagens/qualidade/school_2.svg" />
        <p>
          Qualidade como
          <b>reputação das instituições educacionais</b>
        </p>
      </div>
      <div>
        <img src="../../../assets/imagens/qualidade/blackboard.svg" />
        <p>
          Qualidade como
          <b>recursos e insumos</b>, com financiamento adequado, número adequado
          de professores e qualificação
        </p>
      </div>
      <div>
        <img src="../../../assets/imagens/qualidade/trainees.svg" />
        <p>
          Qualidade como
          <b>processo</b>, na natureza intrainstitucional das suas interações
          entre estudantes, professores e outros educadores
        </p>
      </div>
      <div>
        <img src="../../../assets/imagens/qualidade/books.svg" />
        <p>
          Qualidade como
          <b>conteúdo</b>, relacionado ao corpo de conhecimento, habilidades e
          informações, com o currículo escolar
        </p>
      </div>
      <div>
        <img src="../../../assets/imagens/qualidade/formation.svg" />
        <p>
          Qualidade como
          <b>resultados</b>, associando qualidade ao conhecimento em habilidades
          cognitivas, taxas de ingresso em níveis posteriores de ensino, renda,
          status educacional
        </p>
      </div>
      <div>
        <img src="../../../assets/imagens/qualidade/trainee.svg" />
        <p>
          Qualidade como
          <b>valor agregado</b>,como uma medida de mudança, referindo-se se ao
          impacto, influência ou efeito das instituições nos estudantes
        </p>
      </div>
    </div>
  </div>
  <img src="../../../assets/imagens/qualidade/curve_05_quality.svg" />
</div>
<div class="recursos-insumos-escolares">
  <h1>Recursos e insumos escolares</h1>
  <div class="insumos-monetarios">
    <img src="../../../assets/imagens/qualidade/cost_calculation.svg" />
    <p>
      <b>Insumos Monetários:</b>
      algumas dessas dimensões de qualidade implicam em alocação de recursos
      financeiros para o custeio da remuneração dos profissionais da educação,
      de equipamentos, materiais didáticos, infraestrutura das escolas, para a
      garantia de adequado número de alunos por turma e docente, entre outros
      insumos.
    </p>
  </div>
  <div class="insumos-nao-monetarios">
    <p>
      <b>Insumos não monetários:</b>
      outros aspectos não implicam diretamente em alocação de recursos, mas
      estão relacionadas à gestão da escola e do sistema de ensino, ao
      currículo, ao projeto político-pedagógico, ao ambiente escolar, ao apoio e
      envolvimento da família no aprendizado dos alunos, entre outros
      (WALTENBERG, 2006).
    </p>
    <img src="../../../assets/imagens/qualidade/clipboard.svg" />
  </div>
</div>

<div class="qualidade-versus-condicao">
  <img src="../../../assets/imagens/qualidade/curve_07_quality.svg" />
  <div>
    <h1>Qualidade <i>versus</i> condições de qualidade</h1>
    <div class="content">
      <img src="../../../assets/imagens/qualidade/medal.svg" />
      <div>
        <p>
          Considerando que a educação de qualidade é uma concepção complexa que
          depende da atuação conjunta de diferentes fatores, atores e insumos,
          acreditamos que o financiamento adequado das escolas é um aspecto
          fundamental para garantir uma
          <b>oferta de ensino, em condições de qualidade</b>, ou seja, recursos
          financeiros não garantem uma educação de qualidade (em todos seus
          aspectos e dimensões), mas são essenciais para o custeio de recursos e
          insumos que garantem condições de qualidade (ALVES, 2012).
        </p>
        <p>
          Nesse sentido, o Simulador de Custo-Aluno-Qualidade (SimCAQ) também
          está baseado na concepção de Custo-Aluno Qualidade (CAQ) definida por
          Carreira e Pinto (2007) como o montante de recursos monetários, por
          aluno, necessário para dotar as escolas com as condições de trabalho
          adequadas, como condição necessária, embora não suficiente, para um
          efetivo processo de ensino e aprendizagem.
        </p>
      </div>
    </div>
  </div>
  <img src="../../../assets/imagens/qualidade/curve_08_quality.svg" />
</div>

<div class="oferta-ensino">
  <div class="content">
    <h1>Marcos normativos sobre padrão mínimo de qualidade e CAQ</h1>
    <div>
      <div>
        <p>
          A Emenda Constitucional nº 108/2020, que alterou o artigo 211, consagra o Custo-aluno qualidade (CAQ), como referência para garantir condições adequadas de ensino para as escolas públicas, definindo como uma das funções da União a garantia de “equalização de oportunidades educacionais e padrão mínimo de qualidade de ensino mediante assistência técnica e financeira aos Estados, ao Distrito Federal e aos Municípios” (Brasil, 1988, art. 11; §1º). O padrão mínimo de qualidade deverá considerar as “condições adequadas de oferta e terá como referência o Custo Aluno Qualidade” (Brasil, 1988, art. 11; §2º).
        </p>
        <p>
          A Lei de Diretrizes e Bases da Educação Nacional (LDB/96), Lei nº 9.394/1996, define no Art. 4º, inciso IX “padrões mínimos de qualidade de ensino” como a “variedade e quantidade mínimas, por aluno, de insumos indispensáveis ao desenvolvimento do processo de ensino-aprendizagem adequados à idade e às necessidades específicas de cada estudante, inclusive mediante a provisão de mobiliário, equipamentos e materiais pedagógicos apropriados” (Brasil, 1996, atualizado pela Lei nº 14.333 de 2022). 
        <p>
          Para assegurar o ensino de qualidade, o Art. 74 da LDB/1996 determinou o estabelecimento pela União, com a colaboração dos Estados, do Distrito Federal e dos Municípios, de um <b>”padrão mínimo de oportunidades educacionais para o ensino fundamental, baseado no cálculo do custo mínimo por aluno”</b> (grifo nosso) (Brasil, 1996).
        </p>
        <p>
          A Lei nº 14.113/2020 que regulamenta o novo Fundo de Manutenção e Desenvolvimento da Educação Básica e de Valorização dos Profissionais da Educação (Fundeb) estabelece no Art. 18 que compete à Comissão Intergovernamental de Financiamento para a Educação Básica de Qualidade especificar anualmente as diferenças e as ponderações aplicadas às: <b>”diferentes etapas, modalidades, duração da jornada e tipos de estabelecimento de ensino da educação básica, observado o disposto no art. 9º desta Lei, considerada a correspondência ao custo médio da respectiva etapa, modalidade e tipo de estabelecimento de educação básica”</b> (Brasil, 2020). Igualmente, o Art. 49 da Lei nº 14.113/2020, estabelece que <b>”a União, os Estados, o Distrito Federal e os Municípios deverão assegurar no financiamento da educação básica</b>, previsto no Art. 212 da Constituição Federal, a melhoria da qualidade do ensino, de forma a garantir padrão mínimo de qualidade definido nacionalmente. Ainda no Art. 49; §1º, destaca que dever ser <b>”assegurada a participação popular e da comunidade educacional no processo de definição do padrão nacional de qualidade [...]”</b> (Brasil, 2020).
        </p>
        <p>
          O conceito de CAQ e Custo-aluno qualidade inicial (CAQi) foram incorporados no Plano Nacional de Educação (PNE), Lei no 13.005, de 2014, cuja diretriz VIII, no Art.2o, prevê o “estabelecimento de meta de aplicação de recursos públicos, em educação, como proporção do Produto Interno Bruto - PIB, que assegure atendimento às necessidades de expansão, com padrão de qualidade e equidade” (Brasil, 2014).
        </p>
      </div>
      <div>
        <p>
          A meta 20 do PNE é “ampliar o investimento público em educação pública de forma a atingir, no mínimo, o patamar de 7% (sete por cento) do Produto Interno Bruto - PIB do País no 5o (quinto) ano de vigência desta Lei e, no mínimo, o equivalente a 10% (dez por cento) do PIB ao final do decênio” (Brasil, 2014). Para isso, as estratégias 20.6 estabelece que “no prazo de 2 (dois) anos da vigência deste PNE, será implantado o Custo Aluno-Qualidade Inicial - CAQi, referenciado no conjunto de padrões mínimos estabelecidos na legislação educacional e cujo financiamento será calculado com base nos respectivos insumos indispensáveis ao processo de ensino-aprendizagem e será progressivamente reajustado até a implementação plena do Custo Aluno Qualidade – CAQ.
        </p>
        <p>
          No mesmo sentido, a estratégia 20.7 institui “implementar o Custo Aluno Qualidade - CAQ como parâmetro para o financiamento da educação de todas etapas e modalidades da educação básica, a partir do cálculo e do acompanhamento regular dos indicadores de gastos educacionais com investimentos em qualificação e remuneração do pessoal docente e dos demais profissionais da educação pública, em aquisição, manutenção, construção e conservação de instalações e equipamentos necessários ao ensino e em aquisição de material didático-escolar, alimentação e transporte escolar (grifos nossos) (Brasil, 2014).
        </p>
        <p>
          A Lei Complementar nº 220, de 31 de outubro de 2025, que institui o Sistema Nacional de Educação (SNE) e fixa normas para a cooperação entre União, os Estados, do Distrito Federal e os Municípios (Brasil, 2025a) ao definir as funções integradoras do SNE destaca os “padrões nacionais de qualidade” (art. 10). Sendo uma das funções da Comissão Intergestores Tripartite da Educação (Cite) a pactuação sobre o padrão mínimo de qualidade do ensino na educação básica a que se refere o § 1ºdo art. 211 da Constituição Federal, considerando as “condições adequadas de oferta das etapas, das modalidades e dos tipos de jornada e de estabelecimentos de ensino e a diversidade regional e local das redes de ensino, bem como os respectivos custos diferenciados”. (Brasil, 2025 a, art. 13, inc V.). Para tanto, também compete a Cite a aprovação da metodologia de cálculo do CAQ para a educação básica “com base em estudos técnicos elaborados pelo Instituto Nacional de Estudos e Pesquisas Educacionais Anísio Teixeira (Inep) e, caso necessário, a critérios da Cite, por outras instituições e órgãos públicos por ela designados...” (Brasil, 2025 a, art. 13, inc. VI).
        </p>
        <p>
          De acordo com a Lei do SNE, os padrões mínimos de qualidade da educação, nos termos do §1º do artigo 211 da CF, são padrões nacionais e serão observados em todo o território nacional. No que diz respeito as competências da Cite para o estabelecimento do padrão mínimo de qualidade de ensino e para a aprovação da metodologia do CAQ, a Lei do SNE, define que caberá a Cite publicar resoluções para a efetuação das pactuações entre os entes federados. Portanto, caberá a essa Comissão de gestores a definição dos rumos da implementação do CAQ.
        </p>
      </div>
    </div>
  </div>
</div>

<div class="qualidade-referencias">
  <h2>Referências</h2>
  <div class="qualidade-referencias-inner">
      <p>
        ADAMS, Don. <b>Defining Educational Quality</b>. Arlington, VA, Institute for International Research, 1993.
      </p>
      <p>
        ALVES. Thiago. <b>Desenvolvimento de um modelo de previsão de custos para planejamento de sistemas públicos de educação básica em condições de qualidade</b>: uma aplicação a municípios de Goiás. 353f. Tese (Doutorado em Administração) - Faculdade de Economia, Administração e Contabilidade, Universidade de São Paulo, São Paulo, 2012.
      </p>
      <p>
        BRASIL Constituição (1988). <b>Constituição da República Federativa do Brasil</b>. Brasília, 5 out. 1988.
      </p>
      <p>
        BRASIL. <b>Lei Complementar nº 220, de 31 de outubro de 2025</b>. Institui o Sistema Nacional de Educação (SNE) e fixa normas para a cooperação entre a União, os Estados, o Distrito Federal e os Municípios para elaboração e implementação de políticas, de programas e de ações educacionais, em regime de colaboração. Disponível em: <a href="https://www.planalto.gov.br/ccivil_03/LEIS/LCP/Lcp220.htm">https://www.planalto.gov.br/ccivil_03/LEIS/LCP/Lcp220.htm</a>. Acesso em: 24 nov. 2025.
      </p>
      <p>
        BRASIL. <b>Lei federal nº 9.394</b>. Estabelece as diretrizes e bases da educação nacional. Brasília, 23 dez. 1996.
      </p>
      <p>
        BRASIL. <b>Lei federal nº 13.005</b>. Aprova o Plano Nacional de Educação – PNE e dá outras providências. Brasília, 25 jun. 2014.
      </p>
      <p>
        CARREIRA, Denise; PINTO, José Marcelino Rezende. <b>Custo Aluno Qualidade Inicial</b>: rumo à educação pública de qualidade no Brasil. São Paulo: Global, Campanha Nacional pelo Direito à Educação, 2007.
      </p>
      <p>
        CURY, Carlos Roberto Jamil. A qualidade da educação brasileira como direito. <b>Educação e Sociedade</b>, Campinas, v. 35, n. 129, p.1053-1066, out. 2014.
      </p>
      <p>
        INEP. <b>Problematização da qualidade em pesquisa de custo-aluno-ano em escolas de educação básica</b>: relatório de pesquisa. Instituto Nacional de Estudos e Pesquisas Educacionais Anísio Teixeira. Brasília. 2006.
      </p>
      <p>
        DOURADO, Luiz Fernandes; OLIVEIRA, João Ferreira de. A qualidade da educação: perspectivas e desafios. <b>Cadernos Cedes</b>, Campinas, v. 29, n. 78, ago.2009.
      </p>
      <p>
        OLIVEIRA, Romualdo Portela de; ARAUJO, Gilda Cardoso de. Qualidade do ensino: uma nova dimensão da luta pelo direito à educação. <b>Rev. Bras. Educ.</b>, Rio de Janeiro, n. 28, abr. 2005. Disponível em: <a href="http://www.scielo.br/pdf/rbedu/n28/a02n28.pdf">http://www.scielo.br/pdf/rbedu/n28/a02n28.pdf</a>.
      </p>
      <p>
        WALTENBERG, F. D. Teorias econômicas de oferta de educação: evolução histórica, estado atual e perspectivas. <b>Educação e Pesquisa</b>, v. 32, n. 1, p. 117-136, 2006.
      </p>
  </div>
  <img src="../../../assets/imagens/qualidade/main_curves_down.svg" />
</div>
